import * as React from 'react';
import { Alert } from 'reactstrap';
import { IServiceContacts } from '../../actions/contacts/get-service-contacts.action';
import { mapBusinessUnitToText } from '../../utilities/map-business-unit-to-text';
import { mapRoleCodeToText } from '../../utilities/map-role-code-to-text';
import ContactCard from './components/contact-card';
import SearchBox from './components/search-box';
import { ILxsContactPageViewProps } from './lxs-contact-page';
import { ILxsContactPageFilters } from './lxs-contact-page.data';

const LxsContactPageView: React.FC<ILxsContactPageViewProps> = ({ serviceContacts, resources, context }) => {
    const CLASS_NAME = 'lxs-contact-page';

    const [filters, setFilters] = React.useState<ILxsContactPageFilters>({
        salesOrg: '',
        businessUnit: '',
        role: ''
    });
    const { salesOrg, businessUnit, role } = filters;

    const updateFilter = (key: keyof ILxsContactPageFilters, value: string) => setFilters(prev => ({ ...prev, [key]: value }));

    const mergeDuplicateContacts = (contacts: IServiceContacts[]): IServiceContacts[] => {
        const map = new Map<string, IServiceContacts>();

        contacts.forEach(contact => {
            const key = `${contact.Email}_${contact.Role}`;
            const existing = map.get(key);

            if (!existing) {
                map.set(key, contact);
            } else {
                const combinedSalesAreas = [...existing.SalesAreas, ...contact.SalesAreas];
                const uniqueSalesAreas = combinedSalesAreas.filter(
                    (salesArea, index, self) =>
                        index ===
                        self.findIndex(area => area.SalesOrg === salesArea.SalesOrg && salesArea.DivisionText === salesArea.DivisionText)
                );

                map.set(key, { ...existing, SalesAreas: uniqueSalesAreas });
            }
        });

        return Array.from(map.values());
    };

    const getGroupedContacts = (contacts: IServiceContacts[]) => {
        const mergedContacts = mergeDuplicateContacts(contacts);
        const filtered = mergedContacts.filter(contact => {
            const matchSalesOrg = !salesOrg || contact.SalesAreas.some(salesArea => salesArea.SalesOrgText === salesOrg);
            const matchBusinessUnit =
                !businessUnit || contact.SalesAreas.some(salesArea => mapBusinessUnitToText(salesArea.Division) === businessUnit);
            const matchRole = !role || mapRoleCodeToText(contact.Role, resources) === role;

            return matchSalesOrg && matchBusinessUnit && matchRole;
        });

        const roles = [...new Set(filtered.map(contact => mapRoleCodeToText(contact.Role, resources)))];

        return roles.map(roleText => ({
            role: roleText,
            contacts: filtered.filter(contact => mapRoleCodeToText(contact.Role, resources) === roleText)
        }));
    };

    const groupedContacts = getGroupedContacts(serviceContacts);

    const getUniqueSalesOrgsForFilter = (contacts: IServiceContacts[]): string[] => {
        const salesOrgs = contacts.flatMap(contact => contact.SalesAreas.map(salesArea => salesArea.SalesOrgText));
        return [...new Set(salesOrgs)];
    };

    const getUniqueBusinessUnitsForFilter = (contacts: IServiceContacts[]): string[] => {
        const divisions = contacts.flatMap(contact => contact.SalesAreas.map(salesArea => salesArea.Division));
        const businessUnits = divisions.map(mapBusinessUnitToText);
        return [...new Set(businessUnits)];
    };

    const getRolesForFilter: string[] = [
        ...new Set(
            serviceContacts
                .map(serviceContact => serviceContact.Role)
                .filter(Boolean)
                .map(role => mapRoleCodeToText(role, resources))
        )
    ];

    const displayedContactsCount = groupedContacts.flatMap(group => group.contacts).length;

    return (
        <div className={`${CLASS_NAME}__wrapper`}>
            <div className={`${CLASS_NAME}__search-box__wrapper`}>
                <SearchBox
                    resources={resources}
                    CLASS_NAME={CLASS_NAME}
                    context={context}
                    salesOrgs={getUniqueSalesOrgsForFilter(serviceContacts)}
                    businessUnits={getUniqueBusinessUnitsForFilter(serviceContacts)}
                    filters={filters}
                    roles={getRolesForFilter}
                    onFilterChange={updateFilter}
                />
            </div>
            {displayedContactsCount > 0 && (
                <h3 className={`${CLASS_NAME}__header`}>{`${displayedContactsCount} ${resources.contactPageHeader}`}</h3>
            )}
            {groupedContacts.length > 0 ? (
                <div className={`${CLASS_NAME}__contact-card__wrapper`}>
                    {groupedContacts.flatMap(group =>
                        group.contacts.map(contact => (
                            <ContactCard
                                key={`${contact.Email}_${contact.Role}`}
                                resources={resources}
                                CLASS_NAME={CLASS_NAME}
                                partner={contact}
                            />
                        ))
                    )}
                </div>
            ) : (
                <Alert color='warning'>{resources.contactPageNoContactsToDisplay}</Alert>
            )}
        </div>
    );
};

export default LxsContactPageView;
